/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

export type ViolationSeverity = 'error' | 'warning'
export type ViolationType = 'soft' | 'hard'
export type ViolationCategory = 'dates' | 'availability' | 'time' | 'location'

export interface Violation {
  id: string
  date: string
  constraint: string
  gamesAffected: number
  category: ViolationCategory
  type: ViolationType
  description: string
  severity: ViolationSeverity
}

interface ViolationsSectionProps {
  violations: Violation[]
  onExport?: () => void
  onViewConflicts?: () => void
  onViewWarnings?: () => void
}

// Chevron down/expand icon
const ChevronDownIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.NEUTRAL_50,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M7.41 8.295L12 12.875L16.59 8.295L18 9.705L12 15.705L6 9.705L7.41 8.295Z'
      fill={color}
    />
  </svg>
)

// Chevron right icon for buttons
const ChevronRightIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.PRIMARY_300,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M9.705 6L8.295 7.41L12.875 12L8.295 16.59L9.705 18L15.705 12L9.705 6Z'
      fill={color}
    />
  </svg>
)

// Error icon (circle with exclamation)
const ErrorIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.ERROR_200,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM11 7H13V13H11V7ZM11 15H13V17H11V15Z'
      fill={color}
    />
  </svg>
)

// Warning icon (triangle with exclamation)
const WarningIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.WARNING_200,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 5.99L19.53 19H4.47L12 5.99ZM12 2L1 21H23L12 2ZM13 16H11V18H13V16ZM13 10H11V14H13V10Z'
      fill={color}
    />
  </svg>
)

// Category badge colors
const categoryColors: Record<
  ViolationCategory,
  { bg: string; border: string; text: string }
> = {
  dates: {
    bg: 'rgba(52, 5, 58, 0.3)',
    border: '#dc17ff',
    text: '#dc17ff',
  },
  availability: {
    bg: 'rgba(58, 30, 5, 0.3)',
    border: '#ff8800',
    text: '#ff8800',
  },
  time: {
    bg: 'rgba(58, 41, 5, 0.3)',
    border: colors.WARNING_200,
    text: colors.WARNING_200,
  },
  location: {
    bg: 'rgba(5, 58, 58, 0.3)',
    border: colors.INFO_300,
    text: colors.INFO_300,
  },
}

const categoryLabels: Record<ViolationCategory, string> = {
  dates: 'Dates',
  availability: 'Availability',
  time: 'Time',
  location: 'Location',
}

/**
 * Violations Section Component
 * Displays a table of schedule violations with expandable rows
 * Matches Figma node 15532:3791
 */
const ViolationsSection: React.FC<ViolationsSectionProps> = ({
  violations,
  onExport,
  onViewConflicts,
  onViewWarnings,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Styles
  const containerStyles = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  `

  const headerRowStyles = css`
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    width: 100%;
  `

  const sectionTitleStyles = css`
    flex: 1;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 16.8px;
    letter-spacing: 1.68px;
    text-transform: uppercase;
    color: ${colors.WHITE};
    margin: 0;
  `

  const actionButtonsContainerStyles = css`
    display: flex;
    gap: 8px;
    align-items: center;
  `

  const actionButtonStyles = css`
    display: flex;
    align-items: center;
    padding: 4px 5px 4px 8px;
    background: transparent;
    border: 1px solid ${colors.PRIMARY_300};
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: ${colors.PRIMARY_300}20;
    }
  `

  const actionButtonTextStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20.3px;
    color: ${colors.PRIMARY_300};
    margin: 0;
  `

  const tableContainerStyles = css`
    background: ${colors.SECONDARY_400};
    border: 1px solid ${colors.SECONDARY_200};
    border-radius: 8px;
    overflow: hidden;
  `

  const tableHeaderStyles = css`
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 4px 16px;
    background: ${colors.SECONDARY_300};
  `

  const columnHeaderStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 17.4px;
    color: ${colors.NEUTRAL_70};
    margin: 0;
    text-transform: uppercase;
  `

  const rowStyles = (isEven: boolean) => css`
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 8px 16px;
    background: ${isEven ? colors.SECONDARY_500 : 'transparent'};
  `

  const expandIconStyles = (isExpanded: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    transform: ${isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.2s ease;
  `

  const severityIconStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `

  const cellTextStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20.3px;
    color: ${colors.NEUTRAL_30};
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `

  const categoryBadgeStyles = (category: ViolationCategory) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 4px;
    background: ${categoryColors[category].bg};
    border: 1px solid ${categoryColors[category].border};
    border-radius: 4px;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 17.4px;
    color: ${categoryColors[category].text};
  `

  const typeToggleContainerStyles = css`
    display: flex;
    align-items: center;
    border-radius: 4px;
    overflow: hidden;
  `

  const typeToggleItemStyles = (isActive: boolean, type: ViolationType) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    background: ${isActive
      ? type === 'soft'
        ? colors.INFO_500
        : colors.ERROR_500
      : colors.SECONDARY_400};
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 10px;
    line-height: 12px;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: ${colors.NEUTRAL_30};
  `

  return (
    <div css={containerStyles}>
      {/* Header */}
      <div css={headerRowStyles}>
        <p css={sectionTitleStyles}>VIOLATIONS ({violations.length})</p>
        <div css={actionButtonsContainerStyles}>
          <button type='button' onClick={onExport} css={actionButtonStyles}>
            <p css={actionButtonTextStyles}>Export</p>
            <ChevronRightIcon size={16} color={colors.PRIMARY_300} />
          </button>
          <button
            type='button'
            onClick={onViewConflicts}
            css={actionButtonStyles}
          >
            <p css={actionButtonTextStyles}>View Conflicts</p>
            <ChevronRightIcon size={16} color={colors.PRIMARY_300} />
          </button>
          <button
            type='button'
            onClick={onViewWarnings}
            css={actionButtonStyles}
          >
            <p css={actionButtonTextStyles}>View Warnings</p>
            <ChevronRightIcon size={16} color={colors.PRIMARY_300} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div css={tableContainerStyles}>
        {/* Table Header */}
        <div css={tableHeaderStyles}>
          <p
            css={[
              columnHeaderStyles,
              css`
                width: 184px;
              `,
            ]}
          >
            DATE
          </p>
          <p
            css={[
              columnHeaderStyles,
              css`
                flex: 1;
              `,
            ]}
          >
            CONSTRAINT
          </p>
          <p
            css={[
              columnHeaderStyles,
              css`
                flex: 1;
              `,
            ]}
          >
            GAME AFFECTED
          </p>
          <p
            css={[
              columnHeaderStyles,
              css`
                width: 104px;
              `,
            ]}
          >
            CATEGORY
          </p>
          <p
            css={[
              columnHeaderStyles,
              css`
                width: 104px;
              `,
            ]}
          >
            TYPE
          </p>
          <p
            css={[
              columnHeaderStyles,
              css`
                flex: 1;
              `,
            ]}
          >
            DESCRIPTION
          </p>
        </div>

        {/* Data Rows */}
        {violations.map((violation, index) => {
          const isExpanded = expandedRows.has(violation.id)
          const isEven = index % 2 === 1

          return (
            <div
              key={violation.id}
              css={rowStyles(isEven)}
              onClick={() => toggleRow(violation.id)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggleRow(violation.id)
                }
              }}
            >
              <div css={expandIconStyles(isExpanded)}>
                <ChevronDownIcon size={24} color={colors.NEUTRAL_50} />
              </div>
              <div css={severityIconStyles}>
                {violation.severity === 'error' ? (
                  <ErrorIcon size={24} color={colors.ERROR_200} />
                ) : (
                  <WarningIcon size={24} color={colors.WARNING_200} />
                )}
              </div>
              <p
                css={[
                  cellTextStyles,
                  css`
                    width: 104px;
                  `,
                ]}
              >
                {violation.date}
              </p>
              <p
                css={[
                  cellTextStyles,
                  css`
                    flex: 1;
                  `,
                ]}
              >
                {violation.constraint}
              </p>
              <p
                css={[
                  cellTextStyles,
                  css`
                    flex: 1;
                  `,
                ]}
              >
                {violation.gamesAffected} Games Affected
              </p>
              <div
                css={css`
                  width: 104px;
                  display: flex;
                  align-items: center;
                `}
              >
                <span css={categoryBadgeStyles(violation.category)}>
                  {categoryLabels[violation.category]}
                </span>
              </div>
              <div
                css={css`
                  width: 104px;
                `}
              >
                <div css={typeToggleContainerStyles}>
                  <div
                    css={typeToggleItemStyles(
                      violation.type === 'soft',
                      'soft'
                    )}
                  >
                    SOFT
                  </div>
                  <div
                    css={typeToggleItemStyles(
                      violation.type === 'hard',
                      'hard'
                    )}
                  >
                    HARD
                  </div>
                </div>
              </div>
              <p
                css={[
                  cellTextStyles,
                  css`
                    flex: 1;
                  `,
                ]}
              >
                {violation.description}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ViolationsSection
